import { CREATE_WORKER_URL, USER_WORKER_URL, DELAY_ESTIMATION_URL, GENERATION_API_URL, CDN_URL, WORKER_URL, isSfwSite } from '../config.js';
import { showToast } from '../utils/toast.js';
import { initializeCharacterForm, buildStep3OutfitPrompts, buildStep3OutfitNegatives, lockExistingCharacterOutfits, clearExistingCharacterOutfits } from './forms/characterForm.js';
import { stripCharacterGenderTags, withCharacterGenderTag } from './characterGenderTag.js';
import { initializeCharacterAIAssistant, CHARACTER_AI_SUCCESS_EVENT, CHARACTER_AI_ERROR_EVENT } from './characterAIAssistant.js';
import { initializePromptAutocomplete, initializeOutfitPromptAutocomplete, initializeNegativePromptAutocomplete } from './promptAutocomplete.js';
import { initSfwLoraLock } from './sfwLoraLock.js';
import { initializeVoicePreview } from './utils/filePreview.js';
import { storage } from '../utils/storage.js';
import { initOAuthManager } from '../utils/oauthManager.js';
import { buildKvDetails, createKvRow, flushKvFits, initStep2KvTable, refreshKvCounter, releaseKvRows, rememberCharacterName, KV_VALUE_MAX } from './utils/kvRow.js';
import { getCurrentLanguage, applyTranslationsToContainer, applyGenderTranslations, interpolate } from '../translations.js';
import { sfwModerationMessage } from './sfwModerationError.js';
import { getStylePreference, setStylePreference, getCharacterFormStyle, getNsfwEnabled } from '../utils/userPrefs.js';
import { getBlockedNsfwPromptMessage, stripNsfwTokens } from '../utils/nsfwTags.js';
import fetchCache from '../utils/fetchCache.js';
import { loadDanbooruTags, getFastPreviewBlockingTags, hasFastPreviewBlockingTags, findPremiumAppearanceTag, getBlockedUnrecognizedPromptMessage } from './danbooruTags.js';
import { startCharacterPreviewGeneration, revealCharacterPreview, beginCharacterPreviewRequest, isCurrentCharacterPreviewRequest, isCharacterSlowPreviewQueued, resetCharacterPreviewSessions, setPreviewLoadingState, getStoredQuota, applyCreditsRemaining } from './imageGeneration.js';
import { buildLocalCreditResult, computeCharacterCreationCost, computeImageUnitCost, computeStandaloneImageCost, formatCreditsUsageLine, getCreditPricing, loadCreditPricing } from './creditCostApi.js';
import { adoptWaitEstimate, remainingWaitSeconds } from './waitEstimate.js';
import {
  clearPrivateCharactersCache,
  fetchPrivateCharacters,
  findPrivateCharacterById,
} from '../utils/privateCharactersApi.js';
import {
  fetchOwnedCharactersForEdit,
  fetchOwnedCharacterForEdit,
} from '../utils/ownedItemsApi.js';
import { mountOwnedItemEditPicker } from './ui/ownedItemEditPicker.js';
import { bindPanelHelpTooltips } from './ui/panelHelpTooltip.js';
import { bindMediaCropPanel } from './ui/mediaCropPanel.js';
import { openCharacterCardGalleryPicker } from './ui/characterCardGalleryPicker.js';
import { standaloneImageRequestUrl } from './standaloneHdUpgrade.js';
import { standaloneVideoRequestUrl } from './standaloneMediaApi.js';
import {
  CARD_CROP_OUTPUT_HEIGHT,
  CARD_CROP_OUTPUT_WIDTH,
  CARD_STILL_MAX_KB,
  MAX_CARD_STILL_BYTES,
  MAX_SOURCE_UPLOAD_BYTES,
  SOURCE_MAX_MB,
  blobToDataUrl,
  captureImagePosterDataUrl,
  captureVideoPosterDataUrl,
  getDataUrlDecodedByteLength,
  isAllowedCardGifType,
  isAllowedCardImageType,
  isAllowedCardVideoType,
} from './mediaUploadLimits.js';

const STEP0_GENERATION_TIMEOUT_MS = 120_000;

const waitForCharacterAiSuccess = () => new Promise((resolve, reject) => {
  const cleanup = () => {
    document.removeEventListener(CHARACTER_AI_SUCCESS_EVENT, onSuccess);
    document.removeEventListener(CHARACTER_AI_ERROR_EVENT, onError);
  };
  const onSuccess = () => {
    cleanup();
    resolve(true);
  };
  const onError = (event) => {
    cleanup();
    reject(new Error(event?.detail?.message || 'Generation failed'));
  };
  document.addEventListener(CHARACTER_AI_SUCCESS_EVENT, onSuccess);
  document.addEventListener(CHARACTER_AI_ERROR_EVENT, onError);
});

const handleStep0 = async () => {
  const reqInput = document.getElementById('ai-request');
  const aiReq = document.getElementById('ai-request');
  const step0Loading = document.getElementById('ai-loading');
  const aiSubmit = document.getElementById('submit-ai-request');

  if (!reqInput || !aiReq || !aiSubmit) {
    showToast('AI assistant not ready', 'exclamation-triangle');
    return;
  }

  const text = reqInput.value.trim();
  if (!text) {
    showToast('Please describe who you are looking for', 'exclamation-triangle');
    return;
  }

  // Don't show "Searching…" unless the AI assistant can actually run.
  // The assistant click handler will show the Free Account modal for guests.
  try {
    const oauthManager = initOAuthManager();
    const authed = await oauthManager.isAuthenticated();
    if (!authed) return;
  } catch (e) {
    console.warn('Auth check failed; skipping search UI', e);
    return;
  }

  // Clear any existing draft before new generation
  try {
    storage.remove('character_draft_v1');
  } catch (e) {
    console.warn('Failed to clear draft before generation', e);
  }

  aiReq.value = text;
  if (step0Loading) {
    step0Loading.classList.remove('is-hidden');
  }

  setActiveStep(1);

  const timeoutMs = STEP0_GENERATION_TIMEOUT_MS;
  let didTimeout = false;
  let generationFailed = false;
  try {
    await Promise.race([
      waitForCharacterAiSuccess(),
      new Promise((resolve) => setTimeout(() => {
        didTimeout = true;
        resolve();
      }, timeoutMs)),
    ]);
  } catch {
    generationFailed = true;
  }

  if (step0Loading) {
    step0Loading.classList.add('is-hidden');
  }
  if (didTimeout) {
    showToast('Search timed out. Please try again.', 'exclamation-triangle');
    setActiveStep(0);
    return;
  }
  if (generationFailed) {
    setActiveStep(0);
    return;
  }
  applyGenderTranslations('create-character');
  setActiveStep(1);
};

const state = {
  characterId: null,
  completedSteps: new Set(),
  hasPreview: false,
  currentStep: 0,
  isCharacterPrivate: false,
  isUpdate: false,
  savedLook: null,
  savedStep2: null,
};

const parkCharacterIdentityRow = (slotId) => {
  const row = document.getElementById('character-identity-row');
  const slot = document.getElementById(slotId);
  if (row && slot && row.parentElement !== slot) slot.prepend(row);
};

const applyOwnedCharacterToForm = async (item) => {
  const nameEl = document.getElementById('ai-character-name');
  const genderEl = document.getElementById('ai-character-gender');
  const styleEl = document.getElementById('ai-character-style');
  const visibilityEl = document.getElementById('ai-character-visibility');
  const appearanceEl = document.getElementById('ai-character-prompt');
  const outfitEl = document.getElementById('ai-outfit-prompt');
  const loraIdEl = document.getElementById('lora-id-input');
  const loraStrengthEl = document.getElementById('lora-strength-input');

  if (nameEl) nameEl.value = item.name || '';
  if (genderEl) {
    genderEl.value = item.gender || 'female';
    genderEl.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (styleEl && item.style) {
    styleEl.value = item.style;
    styleEl.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (visibilityEl) visibilityEl.value = item.is_private ? 'private' : 'public';
  if (appearanceEl) appearanceEl.value = stripCharacterGenderTags(item.appearance || '');
  if (outfitEl) outfitEl.value = item.default_outfit || '';
  const appearanceNegativeEl = document.getElementById('ai-appearance-negative');
  const outfitNegativeEl = document.getElementById('ai-outfit-negative');
  if (appearanceNegativeEl) appearanceNegativeEl.value = item.appearance_negative || '';
  if (outfitNegativeEl) outfitNegativeEl.value = item.default_outfit_negative || '';
  if (loraIdEl) loraIdEl.value = item.lora?.id || '';
  if (loraStrengthEl) loraStrengthEl.value = item.lora?.strength != null ? String(item.lora.strength) : '';

  const voiceEl = document.getElementById('ai-character-voice');
  if (voiceEl && item.voice_provider_id) {
    if (typeof voiceEl.setValue === 'function') voiceEl.setValue(item.voice_provider_id);
    else voiceEl.value = item.voice_provider_id;
    voiceEl.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const details = item.details && typeof item.details === 'object' ? item.details : {};
  const kvTable = document.getElementById('step2-kv-table');
  if (kvTable) {
    releaseKvRows(kvTable);
    kvTable.innerHTML = '';
    const entries = Object.entries(details);
    if (entries.length === 0) {
      initStep2KvTable();
    } else {
      entries.forEach(([key, value]) => kvTable.appendChild(createKvRow(key, value == null ? '' : String(value))));
    }
    refreshKvCounter();
  }

  rememberCharacterName(item.name || '');
  snapshotStep2Baseline();

  state.characterId = item.character_id || item.id;
  state.isUpdate = true;
  state.isCharacterPrivate = Boolean(item.is_private);
  state.hasPreview = false;
  state.savedLook = {
    appearance: (appearanceEl?.value || '').trim(),
    outfit: (outfitEl?.value || '').trim(),
    appearanceNegative: (appearanceNegativeEl?.value || '').trim(),
    outfitNegative: (outfitNegativeEl?.value || '').trim(),
    lora: normalizeLookLora(item.lora),
    style: item.style || getCharacterFormStyle(),
    regen: {
      all_count: Number(item.regen?.all_count) || 0,
      all_action_lora_count: Number(item.regen?.all_action_lora_count) || 0,
      outfit0_count: Number(item.regen?.outfit0_count) || 0,
      outfit0_action_lora_count: Number(item.regen?.outfit0_action_lora_count) || 0,
    },
  };
  setLookFieldsLocked(true);
  setUpdateLookChrome(true);
  clearCharacterLookPreview();
  parkCharacterIdentityRow('character-identity-slot-step2');
  appearanceEl?.dispatchEvent(new Event('input', { bubbles: true }));
  void refreshLookCreditInfo();

  try {
    const outfitIds = await fetchCache.fetchJSON(
      `${WORKER_URL}/character/outfits?character_id=${encodeURIComponent(state.characterId)}`
    );
    lockExistingCharacterOutfits(outfitIds);
  } catch (error) {
    console.warn('[Recruit] Failed to lock existing outfits', error);
    lockExistingCharacterOutfits([]);
  }
};

const fillCharacterUpdatedSub = () => {
  const sub = document.querySelector('#character-updated-card .scenario-queued-sub');
  if (!sub) return;
  const lang = getCurrentLanguage() || 'en';
  const t = window.translations?.[lang]?.['create-character'] || window.translations?.en?.['create-character'] || {};
  const name = document.getElementById('ai-character-name')?.value.trim() || '';
  const tpl = t.character_updated_sub
    || 'Your changes are saved and will be applied to all new messages with {name}.';
  sub.textContent = interpolate(tpl, { name });
};

const showCharacterUpdatedPanel = () => {
  setActiveStep(5);
  document.getElementById('character-queued-inline-card')?.classList.add('is-hidden');
  document.getElementById('character-updated-card')?.classList.remove('is-hidden');
  fillCharacterUpdatedSub();
};

const formSnapshotByMode = { fast: null, slow: null };

const previewHasImage = () => {
  const previewContainer = document.getElementById('ai-character-prompt-preview');
  const previewFront = document.querySelector('#ai-character-prompt-preview .card-front');
  const hasBg =
    (previewFront && previewFront.style.backgroundImage && previewFront.style.backgroundImage !== 'none') ||
    (previewContainer && previewContainer.style.backgroundImage && previewContainer.style.backgroundImage !== 'none');
  const hasImg =
    (previewFront && previewFront.querySelector('img')) ||
    (previewContainer && previewContainer.querySelector('img'));
  return Boolean(hasBg || hasImg);
};

const setPreviewYesWaiting = (waiting) => {
  const yesBtn = document.getElementById('step1-submit');
  if (!yesBtn) return;
  yesBtn.disabled = waiting;
  yesBtn.setAttribute('aria-busy', waiting ? 'true' : 'false');
};

const STEP_KEY = 'recruit_current_step';
const isRecruitQueueDebug = () => {
  try { return localStorage.getItem('debug_recruit_queue') === '1'; } catch { return false; }
};
const setStoredStep = (step) => {
  const val = String(step);
  try {
    localStorage.setItem(STEP_KEY, val);
  } catch {
    try { sessionStorage.setItem(STEP_KEY, val); } catch (e) { console.warn('Failed to persist step', e); }
  }
};

const getStoredStep = () => {
  const read = () => {
    try {
      const v = localStorage.getItem(STEP_KEY);
      if (v !== null) return v;
    } catch {}
    try {
      return sessionStorage.getItem(STEP_KEY);
    } catch {}
    return null;
  };
  return read();
};

const chips = {
  1: document.getElementById('step-chip-1'),
  2: document.getElementById('step-chip-2'),
  3: document.getElementById('step-chip-3'),
};

const HIRES_ETA_DISPLAY_BUFFER_SECONDS = 10;

const hiresDisplayDurationMs = (estimatedHiresSeconds) => (
  (Number(estimatedHiresSeconds) > 0)
    ? (Number(estimatedHiresSeconds) + HIRES_ETA_DISPLAY_BUFFER_SECONDS) * 1000
    : 0
);

const syncHeaderReset = (step) => {
  const resetBtn = document.getElementById('reset-draft-btn');
  if (!resetBtn) return;
  resetBtn.classList.toggle('is-hidden', step === 0);
  const showDraft = step === 1;
  resetBtn.querySelector('[data-reset-label="draft"]')?.classList.toggle('is-hidden', !showDraft);
  resetBtn.querySelector('[data-reset-label="another"]')?.classList.toggle('is-hidden', showDraft);
};

const syncHeaderTitle = (step) => {
  const title = document.getElementById('create-character-title');
  if (!title) return;
  const showIntro = step === 0;
  title.querySelector('[data-title-label="intro"]')?.classList.toggle('is-hidden', !showIntro);
  title.querySelector('[data-title-label="short"]')?.classList.toggle('is-hidden', showIntro);
};

const setActiveStep = (step) => {
  state.currentStep = step;
  setStoredStep(step);

  document.querySelectorAll('[data-step]').forEach((panel) => {
    const isActive = Number(panel.dataset.step) === step;
    panel.classList.toggle('is-hidden', !isActive);
    panel.classList.toggle('recruit-panel--active', isActive);
  });
  syncHeaderReset(step);
  syncHeaderTitle(step);

  Object.entries(chips).forEach(([key, chip]) => {
    if (!chip) return;
    const isActive = Number(key) === step;
    const isDone = state.completedSteps.has(Number(key));
    chip.classList.toggle('recruit-chip--active', isActive);
    chip.classList.toggle('recruit-chip--done', isDone);
    chip.classList.toggle('recruit-chip--inactive', !(isActive || isDone));
  });

  if (step === 2) flushKvFits();
  if (step === 4) syncNsfwCardSlotVisibility();
};

const lockForm = (formEl) => {
  if (!formEl || typeof formEl.querySelectorAll !== 'function') return;
  const elements = formEl.querySelectorAll('input, textarea, select, button');
  elements.forEach((el) => {
    el.setAttribute('disabled', 'true');
  });
};


const buildLoraPayload = () => {
  if (!getNsfwEnabled()) return null;
  const loraIdEl = document.getElementById('lora-id-input');
  const loraStrengthEl = document.getElementById('lora-strength-input');
  if (!loraIdEl) return null;
  const loraId = loraIdEl.value.trim();
  if (!loraId) return null;
  const strengthRaw = loraStrengthEl ? loraStrengthEl.value.trim() : '';
  const strengthParsed = strengthRaw ? parseFloat(strengthRaw) : NaN;
  return {
    id: loraId,
    source: 'civitai',
    strength: isNaN(strengthParsed) ? 1.0 : strengthParsed,
  };
};

const EMPTY_REGEN = {
  all_count: 0,
  all_action_lora_count: 0,
  outfit0_count: 0,
  outfit0_action_lora_count: 0,
};

const normalizeLookLora = (lora) => {
  if (!lora || typeof lora !== 'object') return null;
  const id = String(lora.id || '').trim();
  const source = String(lora.source || '').trim();
  if (!id || !source) return null;
  const strengthNum = lora.strength == null || lora.strength === '' ? 1 : Number(lora.strength);
  return { id, source, strength: Number.isFinite(strengthNum) ? strengthNum : 1 };
};

const loraLooksEqual = (left, right) => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.id === right.id && left.source === right.source && left.strength === right.strength;
};

const readLookFields = () => ({
  appearance: document.getElementById('ai-character-prompt')?.value.trim() || '',
  outfit: document.getElementById('ai-outfit-prompt')?.value.trim() || '',
  appearanceNegative: document.getElementById('ai-appearance-negative')?.value.trim() || '',
  outfitNegative: document.getElementById('ai-outfit-negative')?.value.trim() || '',
});

const lookPromptsMatchSaved = () => {
  const saved = state.savedLook;
  if (!saved) return false;
  const current = readLookFields();
  return current.appearance === saved.appearance
    && current.outfit === saved.outfit
    && current.appearanceNegative === (saved.appearanceNegative || '')
    && current.outfitNegative === (saved.outfitNegative || '');
};

const lookRegenQuote = () => {
  const saved = state.savedLook;
  if (!state.isUpdate || !saved) return { total: 0, scope: 'none' };
  const current = readLookFields();
  const appearanceChanged = current.appearance !== saved.appearance
    || current.appearanceNegative !== (saved.appearanceNegative || '');
  const outfitChanged = current.outfit !== saved.outfit
    || current.outfitNegative !== (saved.outfitNegative || '');
  const loraChanged = getNsfwEnabled() && !loraLooksEqual(normalizeLookLora(buildLoraPayload()), saved.lora);
  let scope = 'none';
  if (appearanceChanged || loraChanged) scope = 'all';
  else if (outfitChanged) scope = 'outfit0';
  const regen = saved.regen || EMPTY_REGEN;
  const count = scope === 'all'
    ? Number(regen.all_count) || 0
    : scope === 'outfit0' ? Number(regen.outfit0_count) || 0 : 0;
  const actionLoraCount = scope === 'all'
    ? Number(regen.all_action_lora_count) || 0
    : scope === 'outfit0' ? Number(regen.outfit0_action_lora_count) || 0 : 0;
  const pricing = getCreditPricing();
  const characterHasLora = getNsfwEnabled()
    ? Boolean(normalizeLookLora(buildLoraPayload()))
    : Boolean(saved.lora);
  const unit = computeImageUnitCost({
    characterHasLora,
    actionHasLora: false,
    style: saved.style || 'anime',
    pricing,
  });
  const surcharge = Number(pricing.image_lora_surcharge ?? 10);
  const total = count > 0 ? unit * count + surcharge * actionLoraCount : 0;
  return { total, scope };
};

const setLookFieldsLocked = (locked) => {
  ['ai-character-gender', 'ai-character-style'].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.disabled = locked;
    el.setAttribute('aria-disabled', locked ? 'true' : 'false');
  });
};

const setUpdateLookChrome = (visible) => {
  document.getElementById('skip-look-step')?.classList.toggle('is-hidden', !visible);
  if (!visible) document.getElementById('look-credit-info')?.classList.add('is-hidden');
};

const quoteNewCharacterCreation = () => {
  const gender = document.getElementById('ai-character-gender')?.value || 'female';
  const nsfw = getNsfwEnabled();
  return computeCharacterCreationCost({
    gender,
    style: getCharacterFormStyle(),
    nsfw,
    characterHasLora: nsfw && Boolean(buildLoraPayload()),
  });
};

const refreshStep1CreditInfo = async () => {
  const creditInfo = document.getElementById('step1-credit-info');
  if (!creditInfo) return null;
  if (isSfwSite() || state.isUpdate) {
    creditInfo.classList.add('is-hidden');
    return null;
  }
  await loadCreditPricing();
  const quote = quoteNewCharacterCreation();
  const result = buildLocalCreditResult(quote.total);
  const text = creditInfo.querySelector('span');
  if (text) text.textContent = formatCreditsUsageLine({ total: quote.total, available: result.available_credits });
  creditInfo.classList.remove('is-hidden');
  creditInfo.classList.toggle('insufficient', result.has_enough_credits === false);
  return { quote, result };
};

const refreshLookCreditInfo = async () => {
  const creditInfo = document.getElementById('look-credit-info');
  if (!creditInfo) return;
  if (!state.isUpdate) {
    creditInfo.classList.add('is-hidden');
    return;
  }
  await loadCreditPricing();
  const { total } = lookRegenQuote();
  const result = buildLocalCreditResult(total);
  const text = creditInfo.querySelector('span');
  if (text) text.textContent = formatCreditsUsageLine({ total, available: result.available_credits });
  creditInfo.classList.remove('is-hidden');
  creditInfo.classList.toggle('insufficient', result.has_enough_credits === false);
};

const clearCharacterLookPreview = () => {
  const previewFront = document.querySelector('#ai-character-prompt-preview .card-front');
  const previewContainer = document.getElementById('ai-character-prompt-preview');
  if (previewFront) {
    previewFront.style.backgroundImage = '';
    previewFront.querySelectorAll('img').forEach((img) => img.remove());
    previewFront.querySelector('.preview-placeholder')?.classList.remove('is-hidden');
  }
  if (previewContainer) previewContainer.style.backgroundImage = '';
  state.hasPreview = false;
};

const restoreSavedLook = () => {
  const saved = state.savedLook;
  if (!saved) return;
  const appearanceEl = document.getElementById('ai-character-prompt');
  const outfitEl = document.getElementById('ai-outfit-prompt');
  const loraIdEl = document.getElementById('lora-id-input');
  const loraStrengthEl = document.getElementById('lora-strength-input');
  if (appearanceEl) appearanceEl.value = saved.appearance;
  if (outfitEl) outfitEl.value = saved.outfit;
  const appearanceNegativeEl = document.getElementById('ai-appearance-negative');
  const outfitNegativeEl = document.getElementById('ai-outfit-negative');
  if (appearanceNegativeEl) appearanceNegativeEl.value = saved.appearanceNegative || '';
  if (outfitNegativeEl) outfitNegativeEl.value = saved.outfitNegative || '';
  if (loraIdEl && !loraIdEl.disabled) loraIdEl.value = saved.lora?.id || '';
  if (loraStrengthEl && !loraStrengthEl.disabled) {
    loraStrengthEl.value = saved.lora ? String(saved.lora.strength) : '';
  }
};

const lockPreviewButtonsWhenLookUnchanged = (fastBtn) => {
  if (!fastBtn || !state.isUpdate || !lookPromptsMatchSaved()) return;
  const loraChanged = getNsfwEnabled()
    && !loraLooksEqual(normalizeLookLora(buildLoraPayload()), state.savedLook?.lora);
  if (loraChanged) return;
  fastBtn.disabled = true;
  fastBtn.classList.add('disabled');
  fastBtn.setAttribute('aria-disabled', 'true');
};

const getAuthToken = async () => window.tokenManager?.getValidAccessToken?.() ?? null;

const authHeaders = async () => {
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatEtaDuration = (ms) => {
  const sec = Math.max(0, Math.ceil(ms / 1000));
  if (sec < 90) return `${sec} seconds`;
  const min = Math.ceil(sec / 60);
  return min === 1 ? '1 minute' : `${min} minutes`;
};

const extractPreviewUrlFromRecruit = () => {
  const container = document.getElementById('ai-character-prompt-preview');
  const front = document.querySelector('#ai-character-prompt-preview .card-front');

  const getUrlFromBg = (el) => {
    if (!el) return '';
    const bg = el.style?.backgroundImage || '';
    const m = bg.match(/url\(["']?(.*?)["']?\)/);
    return m ? m[1] : '';
  };

  // Prefer explicit <img>
  const img = (front && front.querySelector('img')) || (container && container.querySelector('img'));
  if (img && img.src) return img.src;

  // Fallback to background-image
  const url = getUrlFromBg(front) || getUrlFromBg(container);
  return url || '';
};

const QUEUE_KEY = 'recruit_character_queue_v1';
let __queuedCardIntervals = { tick: null, poll: null, estimate: null, cdn: null };

const isCharacterEstimateReady = (est) => est?.status === 'success' || est?.status === 'completed';

const safeParseQueueJson = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try { return JSON.parse(raw); } catch { return null; }
};

const clearQueuedCardTimers = () => {
  if (__queuedCardIntervals.tick) clearInterval(__queuedCardIntervals.tick);
  if (__queuedCardIntervals.poll) clearInterval(__queuedCardIntervals.poll);
  if (__queuedCardIntervals.estimate) clearInterval(__queuedCardIntervals.estimate);
  if (__queuedCardIntervals.cdn) clearInterval(__queuedCardIntervals.cdn);
  __queuedCardIntervals.tick = null;
  __queuedCardIntervals.poll = null;
  __queuedCardIntervals.estimate = null;
  __queuedCardIntervals.cdn = null;
};

const getQueuedCharacterState = () => {
  // IMPORTANT: use localStorage explicitly so this survives navigation + full browser restarts.
  // The shared `storage` helper can fall back to sessionStorage in some environments.
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = safeParseQueueJson(raw);
    if (parsed) return parsed;
  } catch { }
  try { return storage.get(QUEUE_KEY) || null; } catch { return null; }
};

const setQueuedCharacterState = (next) => {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(next)); return; } catch { }
  try { storage.set(QUEUE_KEY, next); } catch { }
};

const clearQueuedCharacterState = () => {
  try { localStorage.removeItem(QUEUE_KEY); } catch { }
  try { storage.remove(QUEUE_KEY); } catch { }
};

const notificationCharacterId = (notification) => {
  const meta = notification?.metadata && typeof notification.metadata === 'object' ? notification.metadata : {};
  const params = notification?.params;
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    return meta.character_id || params.character_id || null;
  }
  const arr = Array.isArray(params) ? params : [];
  return meta.character_id || arr[1] || null;
};

const ensureQueuedPanelRootVisible = () => {
  const panel = document.getElementById('character-queued-panel');
  const root = document.getElementById('character-queued-root');
  if (!panel || !root) return null;
  panel.classList.remove('is-hidden');
  return root;
};

const renderQueuedCharacterCardInline = async () => {
  const saved = getQueuedCharacterState();
  if (!saved || !saved.characterId || !saved.startedAt) return;

  const root = ensureQueuedPanelRootVisible();
  if (!root) return;

  // Ensure we are on the completion step so the user sees the card in the right place
  if (state.currentStep !== 5) {
    setActiveStep(5);
  }

  const card = document.getElementById('character-queued-inline-card');
  if (!card) return;

  card.classList.remove('is-hidden');
  clearQueuedCardTimers();

  const heading = card.querySelector('.scenario-queued-heading');
  const sub = card.querySelector('.scenario-queued-sub');
  const img = card.querySelector('#character-queued-media-img');
  const placeholder = card.querySelector('.scenario-queued-media-placeholder');
  const progressBar = card.querySelector('.scenario-queued-progress');
  const progressFill = card.querySelector('.scenario-queued-progress-fill');
  const readyEtaLine = card.querySelector('.scenario-queued-ready-eta');
  const hiresEtaLine = card.querySelector('.scenario-queued-hires-eta');
  const startBtn = card.querySelector('.character-queued-start-btn');
  if (!heading || !sub || !img || !placeholder || !progressBar || !progressFill || !readyEtaLine || !hiresEtaLine || !startBtn) return;

  if (saved.previewUrl) {
    img.src = saved.previewUrl;
    img.alt = saved.characterName ? `Preview for ${saved.characterName}` : 'Character preview';
    img.classList.remove('is-hidden');
    placeholder.classList.add('is-hidden');
  } else {
    img.removeAttribute('src');
    img.classList.add('is-hidden');
    placeholder.classList.remove('is-hidden');
  }

  progressFill.style.width = '5%';
  startBtn.disabled = true;
  startBtn.classList.add('is-hidden');

  applyTranslationsToContainer(card, 'create-character', getCurrentLanguage());
  sub.textContent = interpolate(sub.textContent, { name: saved.characterName || '' });
  const readyEtaTemplate = readyEtaLine.textContent || '';
  const hiresEtaTemplate = hiresEtaLine.textContent || '';

  const durationMs = typeof saved.durationMs === 'number' ? saved.durationMs : 5 * 60 * 1000;
  const hiresDurationMs = typeof saved.hiresDurationMs === 'number' ? saved.hiresDurationMs : 0;
  let isReady = !!saved.ready;
  let isFailed = !!saved.failed;
  // DB sprites done, but CDN publish may still be in flight.
  let isDbDone = !!saved.ready || isFailed;
  // Mutable end timestamps — updated by estimate polls so ETA copy stays accurate.
  let endedAt = saved.startedAt + durationMs;
  let hiresEndedAt = hiresDurationMs > 0 ? Date.now() + hiresDurationMs : 0;
  let spritesTotal = typeof saved.spritesTotal === 'number' ? saved.spritesTotal : 0;
  let progressPct = typeof saved.progressPct === 'number' ? Math.max(5, saved.progressPct) : 5;
  let readyWait = saved.queueRevision
    ? {
      estimatedSeconds: Math.max(0, Math.ceil((endedAt - Date.now()) / 1000)),
      fetchedAt: Date.now(),
      revision: saved.queueRevision,
    }
    : null;
  let hiresWait = saved.hiresRevision && hiresEndedAt > Date.now()
    ? {
      estimatedSeconds: Math.max(0, Math.ceil((hiresEndedAt - Date.now()) / 1000)),
      fetchedAt: Date.now(),
      revision: saved.hiresRevision,
    }
    : null;

  const stopEstimatePoll = () => {
    if (__queuedCardIntervals.estimate) clearInterval(__queuedCardIntervals.estimate);
    __queuedCardIntervals.estimate = null;
  };

  const stopCdnPoll = () => {
    if (__queuedCardIntervals.cdn) clearInterval(__queuedCardIntervals.cdn);
    __queuedCardIntervals.cdn = null;
  };

  const persistEtaState = () => {
    const current = getQueuedCharacterState();
    if (!current) return;
    current.durationMs = Math.max(0, endedAt - current.startedAt);
    current.queueRevision = readyWait?.revision ?? null;
    current.hiresDurationMs = Math.max(0, hiresEndedAt - Date.now());
    current.hiresRevision = hiresWait?.revision ?? null;
    setQueuedCharacterState(current);
  };

  const applyCharacterEstimates = (est) => {
    if (!est) return;
    updateProgressFromEstimate(est);

    if (isCharacterEstimateReady(est)) {
      if (!isDbDone) markDbDone();
    } else {
      readyWait = adoptWaitEstimate(readyWait, {
        estimated_seconds: est.estimated_seconds,
        queue_revision: est.queue_revision,
      });
      endedAt = readyWait.fetchedAt + readyWait.estimatedSeconds * 1000;
    }

    const hiresIncoming = hiresDisplayDurationMs(est.estimated_hires_seconds) / 1000;
    if (hiresIncoming > 0 || est.hires_revision) {
      hiresWait = adoptWaitEstimate(hiresWait, {
        estimated_seconds: hiresIncoming,
        queue_revision: est.hires_revision,
      });
      const remainingHires = remainingWaitSeconds(hiresWait);
      hiresEndedAt = remainingHires > 0
        ? hiresWait.fetchedAt + hiresWait.estimatedSeconds * 1000
        : 0;
    } else {
      hiresWait = null;
      hiresEndedAt = 0;
    }

    persistEtaState();
  };

  const persistProgressState = () => {
    const current = getQueuedCharacterState();
    if (!current) return;
    current.spritesTotal = spritesTotal;
    current.progressPct = progressPct;
    setQueuedCharacterState(current);
  };

  const renderProgressBar = () => {
    const pct = Math.max(5, Math.min(100, progressPct));
    progressBar.setAttribute('aria-valuenow', String(pct));
    progressFill.style.width = `${pct}%`;
  };

  /** Monotone progress from estimate sprites_remaining (not wall-clock). Floored at 5% until complete. */
  const updateProgressFromEstimate = (est) => {
    if (!est) return;
    const remaining = isCharacterEstimateReady(est)
      ? 0
      : Math.max(0, Number(est.sprites_remaining) || 0);
    if (remaining > 0) spritesTotal = Math.max(spritesTotal, remaining);
    if (spritesTotal > 0) {
      const done = Math.max(0, spritesTotal - remaining);
      const nextPct = Math.min(100, Math.max(5, Math.round((done / spritesTotal) * 100)));
      progressPct = Math.max(progressPct, nextPct);
    } else {
      progressPct = Math.max(progressPct, 5);
    }
    if (isCharacterEstimateReady(est) || isDbDone) {
      progressPct = 100;
    }
    renderProgressBar();
    persistProgressState();
  };

  const markReadyFromEndpoint = () => {
    if (isReady || isFailed) return;
    const current = getQueuedCharacterState();
    if (!current) return;
    current.ready = true;
    current.readyAt = Date.now();
    current.progressPct = 100;
    setQueuedCharacterState(current);
    isReady = true;
    isDbDone = true;
    progressPct = 100;
    renderProgressBar();
    stopCdnPoll();
    // Keep estimate poll alive while hires ETA may still update.
    updateCountdown();
  };

  // DB row is enough to start. Hangout reads Postgres if the translated file is not in R2 yet.
  const markDbDone = () => {
    if (isReady || isFailed) return;
    markReadyFromEndpoint();
  };

  const applyFailedUi = (notification) => {
    isFailed = true;
    isReady = false;
    isDbDone = true;
    progressPct = 100;
    renderProgressBar();
    stopCdnPoll();
    stopEstimatePoll();
    if (__queuedCardIntervals.poll) {
      clearInterval(__queuedCardIntervals.poll);
      __queuedCardIntervals.poll = null;
    }
    if (__queuedCardIntervals.tick) {
      clearInterval(__queuedCardIntervals.tick);
      __queuedCardIntervals.tick = null;
    }
    startBtn.disabled = true;
    startBtn.classList.add('is-hidden');
    const lang = getCurrentLanguage() || 'en';
    const t = window.translations?.[lang]?.['create-character'] || window.translations?.en?.['create-character'] || {};
    heading.textContent = notification?.title
      || t.character_generation_failed
      || 'Character creation failed';
    sub.textContent = notification?.message
      || t.character_generation_failed_text
      || 'We encountered an unexpected issue while creating your character. Your credits have been refunded, please feel free to create a new one.';
    readyEtaLine.textContent = '';
    readyEtaLine.hidden = true;
    hiresEtaLine.textContent = '';
    hiresEtaLine.hidden = true;
    const current = getQueuedCharacterState();
    if (current) {
      current.failed = true;
      current.ready = false;
      current.progressPct = 100;
      setQueuedCharacterState(current);
    }
  };

  const syncStartButton = (leftMs) => {
    if (isFailed) {
      startBtn.disabled = true;
      startBtn.classList.add('is-hidden');
      return;
    }

    if (isReady) {
      startBtn.classList.remove('is-hidden');
      startBtn.disabled = false;
      startBtn.textContent = 'Start chatting';
      return;
    }

    if (isDbDone) {
      startBtn.classList.remove('is-hidden');
      startBtn.disabled = true;
      startBtn.textContent = 'Almost ready…';
      return;
    }

    if (leftMs > 0) {
      startBtn.classList.add('is-hidden');
      return;
    }

    startBtn.classList.remove('is-hidden');
    startBtn.disabled = true;
    startBtn.textContent = 'Almost ready…';
  };

  const applyEtaCopy = (readyLeftMs, hiresLeftMs) => {
    if (isFailed) {
      readyEtaLine.textContent = '';
      readyEtaLine.hidden = true;
      hiresEtaLine.textContent = '';
      hiresEtaLine.hidden = true;
      return;
    }

    if (isReady || isDbDone) {
      readyEtaLine.textContent = '';
      readyEtaLine.hidden = true;
    } else if (readyLeftMs > 0) {
      readyEtaLine.hidden = false;
      readyEtaLine.textContent = interpolate(readyEtaTemplate, { time: formatEtaDuration(readyLeftMs) });
    } else {
      readyEtaLine.textContent = '';
      readyEtaLine.hidden = true;
    }

    if (hiresLeftMs > 0) {
      hiresEtaLine.hidden = false;
      hiresEtaLine.textContent = interpolate(hiresEtaTemplate, { time: formatEtaDuration(hiresLeftMs) });
    } else {
      hiresEtaLine.textContent = '';
      hiresEtaLine.hidden = true;
    }
  };

  let hasPolledAtZero = false;

  const updateCountdown = () => {
    const left = Math.max(0, endedAt - Date.now());
    const hiresLeft = hiresEndedAt > 0 ? Math.max(0, hiresEndedAt - Date.now()) : 0;

    if (!isReady && !isDbDone && left <= 0) {
      if (!hasPolledAtZero) {
        hasPolledAtZero = true;
        estimateCharacterDuration(saved.characterId).then((est) => {
          if (!est) return;
          applyCharacterEstimates(est);
          if (endedAt > Date.now()) hasPolledAtZero = false;
        }).catch(() => { });
      }
    }

    applyEtaCopy(left, hiresLeft);
    syncStartButton(left);

    if (isReady && hiresLeft <= 0) stopEstimatePoll();
  };

  renderProgressBar();
  if (isFailed) {
    applyFailedUi();
    return;
  }
  updateCountdown();
  __queuedCardIntervals.tick = setInterval(updateCountdown, 1000);

  if (!startBtn.dataset.bound) {
    startBtn.dataset.bound = 'true';
    startBtn.addEventListener('click', async (e) => {
    e.preventDefault();
    if (startBtn.disabled) return;
    startBtn.disabled = true;
    clearQueuedCardTimers();
    clearQueuedCharacterState();
    const privateParam = saved.isPrivate ? '&is_private=true' : '';
    const trimmedName = typeof saved.characterName === 'string' ? saved.characterName.trim() : '';
    const nameParam = trimmedName ? `&character_name=${encodeURIComponent(trimmedName)}` : '';
    window.location.href = `/rankings.html?action=open_character&character=${encodeURIComponent(String(saved.characterId))}${privateParam}${nameParam}`;
    });
  }

  // ── Notification-based ready detection (fallback) ─────────────────────────
  const checkReadyOnce = async () => {
    const current = getQueuedCharacterState();
    if (!current || current.ready || current.failed || isDbDone || isFailed) return;

    const oauthManager = initOAuthManager();
    const authed = await oauthManager.isAuthenticated();
    if (!authed) return;

    const token = await oauthManager.getValidToken();
    if (!token) return;

    const res = await fetch(`${USER_WORKER_URL}/notifications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ limit: 30, unread_only: false })
    });
    if (!res.ok) return;

    const data = await res.json().catch(() => ({}));
    const notifications = data.notifications || [];

    const match = notifications.find((n) => {
      if (!n || (n.type !== 'character_ready' && n.type !== 'character_generation_failed')) return false;
      const notifCharId = notificationCharacterId(n);
      if (!notifCharId) return false;
      if (String(notifCharId) !== String(current.characterId)) return false;
      const createdAt = n.created_at ? new Date(n.created_at).getTime() : 0;
      return createdAt ? createdAt >= (current.startedAt - 60 * 1000) : true;
    });

    if (!match) return;

    if (match.type === 'character_generation_failed') {
      applyFailedUi(match);
      return;
    }

    markDbDone();

    try {
      const notificationId = match.notification_id || match.id || null;
      if (notificationId) {
        await fetch(`${USER_WORKER_URL}/notifications/read`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ notification_id: notificationId })
        });
      }
    } catch { }
  };

  checkReadyOnce().catch(() => { });
  __queuedCardIntervals.poll = setInterval(() => { checkReadyOnce().catch(() => { }); }, 10000);

  // ── Estimate-based ETA + progress refresh every 60s ───────────────────────
  const pollEstimateOnce = async () => {
    const hiresLeft = hiresEndedAt > 0 ? Math.max(0, hiresEndedAt - Date.now()) : 0;
    if (isReady && hiresLeft <= 0) {
      stopEstimatePoll();
      return;
    }
    const est = await estimateCharacterDuration(saved.characterId).catch(() => null);
    if (!est) return;
    applyCharacterEstimates(est);
    updateCountdown();
  };

  pollEstimateOnce().catch(() => { });
  __queuedCardIntervals.estimate = setInterval(() => { pollEstimateOnce().catch(() => { }); }, 60000);
};

const estimateCharacterDuration = async (characterId) => {
  try {
    const res = await fetch(`${DELAY_ESTIMATION_URL}/estimate-character-duration`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ character_id: characterId })
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
};

const buildQueuedCharacterState = ({ characterId, characterName, previewUrl, existing, estimate } = {}) => {
  const durationMs = (estimate?.estimated_seconds > 0)
    ? estimate.estimated_seconds * 1000
    : (existing?.durationMs || 5 * 60 * 1000);
  const estimatedHiresMs = hiresDisplayDurationMs(estimate?.estimated_hires_seconds);
  const hiresDurationMs = estimatedHiresMs > 0
    ? estimatedHiresMs
    : (existing?.hiresDurationMs || 0);

  return {
    characterId: String(characterId),
    characterName: characterName || existing?.characterName || '',
    previewUrl: previewUrl || existing?.previewUrl || '',
    startedAt: existing?.startedAt || Date.now(),
    durationMs,
    hiresDurationMs,
    spritesTotal: (estimate?.sprites_remaining > 0)
      ? Math.max(Number(existing?.spritesTotal) || 0, Number(estimate.sprites_remaining))
      : (existing?.spritesTotal || 0),
    progressPct: Math.max(5, existing?.progressPct || 5),
    ready: !!existing?.ready,
    readyAt: existing?.readyAt || null,
    isPrivate: !!existing?.isPrivate || !!state.isCharacterPrivate,
    queueRevision: estimate?.queue_revision || existing?.queueRevision || null,
    hiresRevision: estimate?.hires_revision || existing?.hiresRevision || null,
  };
};

const upsertQueuedCharacterStateFromCompletion = async ({ characterId, characterName, previewUrl } = {}) => {
  if (!characterId) return;

  setQueuedCharacterState(buildQueuedCharacterState({
    characterId,
    characterName,
    previewUrl,
    existing: getQueuedCharacterState(),
  }));
  await renderQueuedCharacterCardInline();

  const estimate = await estimateCharacterDuration(characterId).catch(() => null);
  if (!estimate) return;

  setQueuedCharacterState(buildQueuedCharacterState({
    characterId,
    characterName,
    previewUrl,
    existing: getQueuedCharacterState(),
    estimate,
  }));
  await renderQueuedCharacterCardInline();
};


const getCharacterCreationLimitMessage = (limitInfo) => {
  const lang = window.getCurrentLanguage?.() ?? 'en';
  const tpl = window.translations?.[lang]?.['create-character']?.character_creation_limit_reached
    ?? 'Character creation limit reached ({{used}}/{{limit}} this billing period).';
  const used = limitInfo?.current_usage ?? '?';
  const limit = limitInfo?.creation_limit ?? '?';
  return tpl.replace('{{used}}', String(used)).replace('{{limit}}', String(limit));
};

const isCharacterCreationLimitError = (err) => {
  if (!err || typeof err !== 'object') return false;
  const body = err.body;
  if (body?.limit_info && body.limit_info.can_create === false) return true;
  if (body?.creation_limit != null && body?.current_usage != null) return true;
  const message = String(err.message || body?.error || body?.error_message || '').toLowerCase();
  return message.includes('character creation limit');
};

const postJSON = async (path, body) => {
  const token = await getAuthToken();
  if (!token) {
    showToast('Please log in to continue.', 'exclamation-triangle');
    throw new Error('Authentication required');
  }

  const json = await fetchCache.fetchJSON(`${CREATE_WORKER_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body,
  });

  if (json.success === false) {
    const errMsg = json.error || json.error_message || 'Request failed';
    throw new Error(errMsg);
  }
  return json;
};

const handleStep1 = async (e) => {
  e?.preventDefault?.();
  const name = document.getElementById('ai-character-name').value.trim();
  const gender = document.getElementById('ai-character-gender').value;
  const style = getCharacterFormStyle();
  const visibility = document.getElementById('ai-character-visibility')?.value || 'public';
  const isCharacterPrivate = visibility === 'private';
  const appearance = document.getElementById('ai-character-prompt').value.trim();
  const defaultOutfit = document.getElementById('ai-outfit-prompt').value.trim();
  const previewModal = document.getElementById('preview-modal');

  // Re-evaluate preview presence to avoid stale flag (check container and child)
  const previewContainer = document.getElementById('ai-character-prompt-preview');
  const previewFront = document.querySelector('#ai-character-prompt-preview .card-front');
  const hasBg =
    (previewFront && previewFront.style.backgroundImage && previewFront.style.backgroundImage !== 'none') ||
    (previewContainer && previewContainer.style.backgroundImage && previewContainer.style.backgroundImage !== 'none');
  const hasImg =
    (previewFront && previewFront.querySelector('img')) ||
    (previewContainer && previewContainer.querySelector('img'));
  if (hasBg || hasImg) state.hasPreview = true;

  if (state.isUpdate && lookRegenQuote().scope === 'none') {
    if (previewModal) previewModal.classList.remove('is-active');
    setActiveStep(2);
    return;
  }

  if (!state.hasPreview) {
    showToast('Generate a preview image with AI before validating Step 1.', 'exclamation-triangle');
    return;
  }

  if (!name || !appearance) {
    showToast('Name and appearance are required.', 'exclamation-triangle');
    return;
  }

  const appearanceNegative = document.getElementById('ai-appearance-negative')?.value?.trim() || '';
  const defaultOutfitNegative = document.getElementById('ai-outfit-negative')?.value?.trim() || '';
  const step1PromptText = [appearance, defaultOutfit, appearanceNegative, defaultOutfitNegative].filter(Boolean).join(', ');
  const nsfwBlock = getBlockedNsfwPromptMessage(step1PromptText);
  if (nsfwBlock) {
    showToast(nsfwBlock, 'exclamation-triangle', { duration: 0, closable: true });
    return;
  }
  const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage(step1PromptText);
  if (unrecognizedBlock) {
    showToast(unrecognizedBlock, 'exclamation-triangle', { duration: 0, closable: true });
    return;
  }

  const lora = getNsfwEnabled() ? buildLoraPayload() : null;
  const appearanceForWorker = withCharacterGenderTag(appearance, gender);

  if (state.isUpdate) {
    try {
      const res = await postJSON('/recruit/update-look', {
        character_id: state.characterId,
        character_image_prompt: stripNsfwTokens(appearanceForWorker) || appearanceForWorker,
        default_outfit: defaultOutfit ? (stripNsfwTokens(defaultOutfit) || defaultOutfit) : null,
        appearance_negative: appearanceNegative ? (stripNsfwTokens(appearanceNegative) || appearanceNegative) : null,
        default_outfit_negative: defaultOutfitNegative ? (stripNsfwTokens(defaultOutfitNegative) || defaultOutfitNegative) : null,
        lora,
      });
      applyCreditsRemaining(res.sprite_credits_remaining ?? res.data?.sprite_credits_remaining);
      state.completedSteps.add(1);
      lockForm(document.getElementById('step1-form'));
      if (previewModal) previewModal.classList.remove('is-active');
      setActiveStep(2);
      const creditsUsed = Number(res.credits_used ?? res.data?.credits_used ?? 0);
      showToast(
        creditsUsed > 0 ? `Look saved. ${creditsUsed} credits used to regenerate images.` : 'Look saved.',
        'check-circle'
      );
    } catch (err) {
      console.error('Update look error', err);
      applyCreditsRemaining(err.body?.sprite_credits_remaining);
      showToast(err.message || 'Failed to update character look', 'exclamation-triangle');
    }
    return;
  }

  if (!isSfwSite()) {
    const creditCheck = await refreshStep1CreditInfo();
    if (creditCheck && creditCheck.result.has_enough_credits === false) {
      const required = creditCheck.quote.total;
      const available = creditCheck.result.available_credits;
      showToast(`Insufficient credits. Required: ${required}, available: ${available}.`, 'exclamation-triangle');
      return;
    }
  }

  try {
    const res = await postJSON('/recruit/step1', {
      name,
      gender,
      style,
      character_image_prompt: stripNsfwTokens(appearanceForWorker) || appearanceForWorker,
      default_outfit: defaultOutfit ? (stripNsfwTokens(defaultOutfit) || defaultOutfit) : null,
      appearance_negative: appearanceNegative ? (stripNsfwTokens(appearanceNegative) || appearanceNegative) : null,
      default_outfit_negative: defaultOutfitNegative ? (stripNsfwTokens(defaultOutfitNegative) || defaultOutfitNegative) : null,
      ...(lora ? { lora } : {}),
      is_character_private: isCharacterPrivate,
    });
    state.characterId = res.data?.character_id || res.character_id;
    state.isCharacterPrivate = isCharacterPrivate;
    applyCreditsRemaining(res.sprite_credits_remaining ?? res.data?.sprite_credits_remaining);
    state.completedSteps.add(1);
    lockForm(document.getElementById('step1-form'));

    // Close modal on success
    if (previewModal) previewModal.classList.remove('is-active');

    setActiveStep(2);
    const lang = window.getCurrentLanguage?.() ?? 'en';
    const step1Msg = window.translations?.[lang]?.['create-character']?.step1_character_created_message
      ?? 'Your character has been created. While we prepare the images, you can specify more information about your character, and even request additional outfits (next step).';
    showToast(step1Msg, 'success', { duration: 0, closable: true });

    // Reset image generation quota — character creation unlocks a fresh set of requests
    try {
      const oauthManager = initOAuthManager();
      const token = await oauthManager.getToken();
      if (token && GENERATION_API_URL) {
        fetch(`${GENERATION_API_URL}/user-gen/reset-quota`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(() => {});
        try { localStorage.removeItem('user_gen_quota'); } catch {}
        document.dispatchEvent(new CustomEvent('genQuotaUpdated', { detail: null }));
      }
    } catch (e) {
      console.warn('[Recruit] Quota reset failed (non-critical):', e.message);
    }
  } catch (err) {
    console.error('Step1 error', err);
    applyCreditsRemaining(err.body?.sprite_credits_remaining);
    if (isCharacterCreationLimitError(err)) {
      const limitInfo = err.body?.limit_info ?? err.body;
      showToast(getCharacterCreationLimitMessage(limitInfo), 'exclamation-triangle');
      return;
    }
    if (err.status === 402 || err.body?.code === 'insufficient_credits') {
      const required = err.body?.required;
      const available = err.body?.available ?? 0;
      showToast(
        required != null ? `Insufficient credits. Required: ${required}, available: ${available}.` : (err.message || 'Insufficient credits'),
        'exclamation-triangle'
      );
      return;
    }
    showToast(err.message || 'Failed to save step 1', 'exclamation-triangle');
  }
};

const readStep2Voice = () => {
  const voiceEl = document.getElementById('ai-character-voice');
  if (!voiceEl) return null;
  const raw = typeof voiceEl.getValue === 'function' ? voiceEl.getValue() : voiceEl.value;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed || null;
};

const detailsMatch = (left, right) => {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]);
};

const snapshotStep2Baseline = () => {
  const name = document.getElementById('ai-character-name')?.value.trim() || '';
  const visibility = document.getElementById('ai-character-visibility')?.value || 'public';
  state.savedStep2 = {
    name,
    voice: readStep2Voice(),
    isPrivate: visibility === 'private',
    details: buildKvDetails(),
  };
};

const finishStep2 = (isCharacterPrivate) => {
  state.isCharacterPrivate = isCharacterPrivate;
  state.completedSteps.add(2);
  lockForm(document.getElementById('step2-form'));
  setActiveStep(3);
  showToast(state.isUpdate ? 'Character updated.' : 'Step 2 saved.', 'check-circle');
};

const handleStep2 = async (e) => {
  e.preventDefault();
  if (!state.characterId) {
    showToast('Complete step 1 first.', 'exclamation-triangle');
    return;
  }

  const name = document.getElementById('ai-character-name')?.value.trim() || '';
  if (!name) {
    showToast('Name is required.', 'exclamation-triangle');
    return;
  }
  const visibility = document.getElementById('ai-character-visibility')?.value || 'public';
  const isCharacterPrivate = visibility === 'private';
  const voiceVal = readStep2Voice();

  let details;
  try {
    details = buildKvDetails();
  } catch (err) {
    showToast(err.message, 'exclamation-triangle');
    return;
  }

  const saved = state.isUpdate ? state.savedStep2 : null;
  const detailsUnchanged = Boolean(saved && detailsMatch(details, saved.details));
  if (
    detailsUnchanged &&
    name === saved.name &&
    voiceVal === saved.voice &&
    isCharacterPrivate === saved.isPrivate
  ) {
    finishStep2(isCharacterPrivate);
    return;
  }

  if (!detailsUnchanged) {
    if (JSON.stringify(details).length > 3000) {
      showToast('Character details are too long (max 3000 characters). Shorten or remove some fields.', 'exclamation-triangle');
      return;
    }
    const oversizedValue = Object.entries(details).find(([, val]) => typeof val === 'string' && val.length > KV_VALUE_MAX);
    if (oversizedValue) {
      showToast(`Value for field "${oversizedValue[0]}" is too long (max ${KV_VALUE_MAX} characters).`, 'exclamation-triangle');
      return;
    }
  }

  const payload = {
    character_id: state.characterId,
    name,
    voice_provider_id: voiceVal,
    is_character_private: isCharacterPrivate,
    ...(detailsUnchanged ? {} : { details }),
  };

  try {
    await postJSON('/recruit/step2', payload);
    if (state.isUpdate && state.savedStep2) {
      state.savedStep2 = {
        name,
        voice: voiceVal,
        isPrivate: isCharacterPrivate,
        details: detailsUnchanged ? state.savedStep2.details : details,
      };
    }
    finishStep2(isCharacterPrivate);
  } catch (err) {
    console.error('Step2 error', err);
    showToast(sfwModerationMessage(err) || err.message || 'Failed to save step 2', 'exclamation-triangle');
  }
};

const handleStep3 = async (e) => {
  e.preventDefault();
  if (!state.characterId) {
    showToast('Complete previous steps first.', 'exclamation-triangle');
    return;
  }

  const grid = document.getElementById('char-outfits-grid');
  const selectedFromGrid = grid
    ? Array.from(grid.querySelectorAll('.char-outfits-item.selected:not(.char-outfits-item--locked)'))
        .map((el) => parseInt(el.getAttribute('data-outfit-id'), 10))
        .filter((id) => !Number.isNaN(id) && id > 1)
    : [];

  if (selectedFromGrid.length === 0) {
    state.completedSteps.add(3);
    setActiveStep(4);
    showToast('No extra outfits added.', 'check-circle');
    return;
  }

  try {
    const outfit_prompts = buildStep3OutfitPrompts(selectedFromGrid);
    const outfit_negatives = buildStep3OutfitNegatives(selectedFromGrid);
    const joinedPrompts = [...outfit_prompts, ...outfit_negatives].join(', ');
    const nsfwBlock = getBlockedNsfwPromptMessage(joinedPrompts);
    if (nsfwBlock) {
      showToast(nsfwBlock, 'exclamation-triangle', { duration: 0, closable: true });
      return;
    }
    const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage(joinedPrompts);
    if (unrecognizedBlock) {
      showToast(unrecognizedBlock, 'exclamation-triangle', { duration: 0, closable: true });
      return;
    }
    await postJSON('/recruit/step3', {
      character_id: state.characterId,
      outfit_ids: selectedFromGrid,
      outfit_prompts: outfit_prompts.map((prompt) => stripNsfwTokens(prompt) || prompt),
      outfit_negatives: outfit_negatives.map((prompt) => (prompt ? (stripNsfwTokens(prompt) || prompt) : '')),
    });
    state.completedSteps.add(3);
    lockForm(document.getElementById('step3-form'));
    setActiveStep(4);
    showToast('Outfits saved.', 'check-circle');
  } catch (err) {
    console.error('Step3 error', err);
    showToast(err.message || 'Failed to save step 3', 'exclamation-triangle');
  }
};

const createEmptyCardSlot = () => ({
  stillDataUrl: null,
  videoDataUrl: null,
  ext: null,
  objectUrl: null,
  dirty: false,
  clear: false,
  galleryOutputKey: null,
  galleryMediaKind: null,
  crop: null,
});

const pendingCards = {
  sfw: createEmptyCardSlot(),
  nsfw: createEmptyCardSlot()
};

const cardGallerySelection = { sfw: '', nsfw: '' };

let activeCardSlot = 'sfw';

const CARD_UNSUPPORTED_MSG = 'Use a JPEG, PNG, WebP, GIF, WebM, or MP4 file.';

const cardTranslation = (key, fallback) => {
  const lang = getCurrentLanguage() || 'en';
  const t = window.translations?.[lang]?.['create-character'] || window.translations?.en?.['create-character'] || {};
  return t[key] || fallback;
};

const showCardSourceTooLarge = () => {
  showToast(
    interpolate(cardTranslation('card_too_large', 'File must be {maxMb} MB or smaller.'), { maxMb: SOURCE_MAX_MB }),
    'exclamation-triangle'
  );
};

const showCardStillTooLarge = () => {
  showToast(
    interpolate(cardTranslation('card_output_too_large', 'Cropped image must be {maxKb} KB or smaller.'), { maxKb: CARD_STILL_MAX_KB }),
    'exclamation-triangle'
  );
};

const previewElForSlot = (slot) => document.getElementById(`character-card-preview-${slot}`);

const resetCardPreview = (slot) => {
  const preview = previewElForSlot(slot);
  if (!preview) return;
  preview.querySelectorAll('img, video').forEach((node) => {
    if (node.closest('.media-crop-viewport')) return;
    node.remove();
  });
  preview.querySelector('.preview-placeholder')?.classList.remove('is-hidden');
  preview.querySelector('.preview-surface-clear')?.classList.add('is-hidden');
};

const syncNsfwCardSlotVisibility = () => {
  const nsfwSlot = document.getElementById('character-card-slot-nsfw');
  const showNsfw = getNsfwEnabled();
  nsfwSlot?.classList.toggle('is-hidden', !showNsfw);
  if (!showNsfw) {
    pendingCards.nsfw = createEmptyCardSlot();
    cardGallerySelection.nsfw = '';
    resetCardPreview('nsfw');
  }
};

const resetPendingCards = () => {
  for (const slot of ['sfw', 'nsfw']) {
    if (pendingCards[slot]?.objectUrl) URL.revokeObjectURL(pendingCards[slot].objectUrl);
    pendingCards[slot] = createEmptyCardSlot();
    cardGallerySelection[slot] = '';
    resetCardPreview(slot);
  }
};

const clearCardSlot = (slot) => {
  const current = pendingCards[slot];
  if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
  pendingCards[slot] = createEmptyCardSlot();
  pendingCards[slot].dirty = true;
  pendingCards[slot].clear = true;
  cardGallerySelection[slot] = '';
  resetCardPreview(slot);
};

const setCharacterCardPreview = (slot, url, isVideo) => {
  const preview = previewElForSlot(slot);
  if (!preview || !url) return;
  preview.querySelectorAll('img, video').forEach((node) => {
    if (node.closest('.media-crop-viewport')) return;
    node.remove();
  });
  preview.querySelector('.preview-placeholder')?.classList.add('is-hidden');
  preview.querySelector('.preview-surface-clear')?.classList.remove('is-hidden');
  if (isVideo) {
    const video = document.createElement('video');
    video.src = url;
    video.className = 'character-card-video-preview';
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;
    preview.appendChild(video);
    return;
  }
  const img = document.createElement('img');
  img.src = url;
  img.alt = slot === 'nsfw' ? 'NSFW home page card preview' : 'SFW home page card preview';
  img.className = 'preview-img';
  preview.appendChild(img);
};

const buildCardSlotPayload = (slot) => {
  const pending = pendingCards[slot];
  if (!pending?.dirty) return undefined;
  if (pending.clear) return { clear: true };
  if (pending.galleryOutputKey) {
    const payload = {
      gallery_output_key: pending.galleryOutputKey,
      media_kind: pending.galleryMediaKind === 'video' ? 'video' : 'image',
    };
    if (pending.crop) payload.crop = pending.crop;
    return payload;
  }
  if (!pending.stillDataUrl) return { clear: true };
  return {
    still_data_url: pending.stillDataUrl,
    video_data_url: pending.videoDataUrl,
    card_ext: pending.ext
  };
};

const finishRecruitAfterCard = async () => {
  state.completedSteps.add(4);
  if (state.isUpdate) {
    showCharacterUpdatedPanel();
    showToast('Character updated.', 'check-circle');
    return;
  }
  setActiveStep(5);
  try {
    const characterName = document.getElementById('ai-character-name')?.value?.trim() || '';
    const previewUrl = extractPreviewUrlFromRecruit();
    await upsertQueuedCharacterStateFromCompletion({ characterId: state.characterId, characterName, previewUrl });
  } catch (error) {
    console.warn('Failed to show character queued card', error);
  }
};

let step4RequestPending = false;

const setStep4RequestPending = (pending) => {
  step4RequestPending = pending;
  const step4Btn = document.getElementById('step4-submit');
  if (step4Btn) {
    step4Btn.disabled = pending;
    step4Btn.setAttribute('aria-disabled', pending ? 'true' : 'false');
    step4Btn.classList.toggle('disabled', pending);
  }
  ['sfw', 'nsfw'].forEach((slot) => {
    const input = document.getElementById(`character-card-upload-${slot}`);
    const galleryBtn = document.getElementById(`character-card-gallery-${slot}`);
    const label = document.querySelector(`label[for="character-card-upload-${slot}"]`);
    if (input) input.disabled = pending;
    if (galleryBtn) {
      galleryBtn.disabled = pending;
      galleryBtn.setAttribute('aria-disabled', pending ? 'true' : 'false');
    }
    if (!label) return;
    label.setAttribute('aria-disabled', pending ? 'true' : 'false');
    label.parentElement?.classList.toggle('is-crop-locked', pending);
  });
};

const handleStep4Submit = async () => {
  if (step4RequestPending) return;
  if (!state.characterId) {
    showToast('Complete previous steps first.', 'exclamation-triangle');
    return;
  }
  const allowNsfw = getNsfwEnabled();
  const sfw = buildCardSlotPayload('sfw');
  const nsfw = allowNsfw ? buildCardSlotPayload('nsfw') : undefined;
  if (!sfw && !nsfw) {
    await finishRecruitAfterCard();
    return;
  }
  setStep4RequestPending(true);
  try {
    await postJSON('/recruit/step4', {
      character_id: state.characterId,
      sfw,
      nsfw
    });
    await finishRecruitAfterCard();
  } catch (error) {
    setStep4RequestPending(false);
    console.error('Step4 card upload error', error);
    const message = sfwModerationMessage(error) || error.message || 'Failed to save home page card';
    showToast(message, 'exclamation-triangle');
  }
};

const bindCharacterCardUpload = () => {
  const cropPanel = document.getElementById('character-card-crop-panel');
  const viewportHost = document.getElementById('character-card-crop-viewport-host');
  const viewportEl = document.getElementById('character-card-crop-viewport');
  const imageEl = document.getElementById('character-card-crop-image');
  const zoomEl = document.getElementById('character-card-crop-zoom');
  const applyBtn = document.getElementById('character-card-crop-apply');
  const cancelBtn = document.getElementById('character-card-crop-cancel');
  const sfwInput = document.getElementById('character-card-upload-sfw');
  const nsfwInput = document.getElementById('character-card-upload-nsfw');
  const step4Btn = document.getElementById('step4-submit');
  const uploadLabels = [
    document.querySelector('label[for="character-card-upload-sfw"]'),
    document.querySelector('label[for="character-card-upload-nsfw"]')
  ];
  const galleryButtons = [
    document.getElementById('character-card-gallery-sfw'),
    document.getElementById('character-card-gallery-nsfw')
  ];
  const setCardCropBusy = (busy) => {
    [sfwInput, nsfwInput, step4Btn, ...galleryButtons].forEach((el) => {
      if (el) el.disabled = busy;
    });
    uploadLabels.forEach((label) => {
      if (!label) return;
      label.setAttribute('aria-disabled', busy ? 'true' : 'false');
      label.parentElement?.classList.toggle('is-crop-locked', busy);
    });
  };
  let galleryCropDraft = null;

  const cardCrop = bindMediaCropPanel({
    viewportEl,
    imageEl,
    zoomEl,
    outputWidth: CARD_CROP_OUTPUT_WIDTH,
    outputHeight: CARD_CROP_OUTPUT_HEIGHT,
    mimeType: 'image/webp',
    quality: 0.9
  });

  const hideCrop = () => {
    cardCrop.destroy();
    cropPanel?.classList.add('is-hidden');
    previewElForSlot('sfw')?.classList.remove('is-cropping');
    previewElForSlot('nsfw')?.classList.remove('is-cropping');
    if (viewportHost && viewportEl && viewportEl.parentElement !== viewportHost) {
      viewportHost.appendChild(viewportEl);
    }
    if (sfwInput) sfwInput.value = '';
    if (nsfwInput) nsfwInput.value = '';
    galleryCropDraft = null;
    setCardCropBusy(false);
  };

  const assignSlotMedia = (slot, {
    stillDataUrl = null,
    videoDataUrl = null,
    ext = null,
    previewUrl,
    isVideoPreview,
    galleryOutputKey = null,
    galleryMediaKind = null,
    crop = null,
  }) => {
    const previous = pendingCards[slot];
    if (previous?.objectUrl && previous.objectUrl !== previewUrl) {
      URL.revokeObjectURL(previous.objectUrl);
    }
    pendingCards[slot] = {
      stillDataUrl,
      videoDataUrl,
      ext,
      objectUrl: previewUrl?.startsWith('blob:') ? previewUrl : null,
      dirty: true,
      clear: false,
      galleryOutputKey,
      galleryMediaKind,
      crop,
    };
    cardGallerySelection[slot] = galleryOutputKey ? `${galleryMediaKind}:${galleryOutputKey}` : '';
    setCharacterCardPreview(slot, previewUrl, isVideoPreview);
  };

  const handleAnimatedFile = async (slot, file) => {
    const objectUrl = URL.createObjectURL(file);
    try {
      let stillDataUrl;
      if (isAllowedCardGifType(file.type)) {
        const img = document.createElement('img');
        img.decoding = 'async';
        await new Promise((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load GIF'));
          img.src = objectUrl;
        });
        stillDataUrl = captureImagePosterDataUrl(img, CARD_CROP_OUTPUT_WIDTH, CARD_CROP_OUTPUT_HEIGHT);
      } else {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        await new Promise((resolve, reject) => {
          video.onloadeddata = () => resolve();
          video.onerror = () => reject(new Error('Failed to load video'));
          video.src = objectUrl;
        });
        if (video.readyState < 2) {
          await new Promise((resolve) => {
            video.onseeked = () => resolve();
            video.currentTime = 0.05;
          });
        }
        stillDataUrl = captureVideoPosterDataUrl(video, CARD_CROP_OUTPUT_WIDTH, CARD_CROP_OUTPUT_HEIGHT);
      }
      if (getDataUrlDecodedByteLength(stillDataUrl) > MAX_CARD_STILL_BYTES) {
        showCardStillTooLarge();
        URL.revokeObjectURL(objectUrl);
        return;
      }
      const videoDataUrl = await blobToDataUrl(file);
      const ext = isAllowedCardGifType(file.type)
        ? 'gif'
        : (file.type.includes('mp4') ? 'mp4' : 'webm');
      assignSlotMedia(slot, {
        stillDataUrl,
        videoDataUrl,
        ext,
        previewUrl: objectUrl,
        isVideoPreview: isAllowedCardVideoType(file.type)
      });
      hideCrop();
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      console.error('Card animated load failed:', error);
      showToast(CARD_UNSUPPORTED_MSG, 'exclamation-triangle');
    }
  };

  const beginStillCrop = async (slot, dataUrl) => {
    activeCardSlot = slot;
    const preview = previewElForSlot(slot);
    previewElForSlot('sfw')?.classList.remove('is-cropping');
    previewElForSlot('nsfw')?.classList.remove('is-cropping');
    if (preview && viewportEl) {
      preview.classList.add('is-cropping');
      preview.appendChild(viewportEl);
    }
    cropPanel?.classList.remove('is-hidden');
    if (cropPanel) setCardCropBusy(true);
    await cardCrop.load(dataUrl);
  };

  const handleUploadChange = async (slot, event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (!file) return;
    if (file.size > MAX_SOURCE_UPLOAD_BYTES) {
      showCardSourceTooLarge();
      input.value = '';
      return;
    }
    activeCardSlot = slot;
    galleryCropDraft = null;
    if (isAllowedCardGifType(file.type) || isAllowedCardVideoType(file.type)) {
      await handleAnimatedFile(slot, file);
      return;
    }
    if (!isAllowedCardImageType(file.type)) {
      showToast(CARD_UNSUPPORTED_MSG, 'exclamation-triangle');
      input.value = '';
      return;
    }
    try {
      const dataUrl = await blobToDataUrl(file);
      await beginStillCrop(slot, dataUrl);
    } catch (error) {
      console.error('Card crop load failed:', error);
      showToast(CARD_UNSUPPORTED_MSG, 'exclamation-triangle');
      hideCrop();
    }
  };

  const applyGalleryItem = async (slot, item) => {
    if (!item?.output_key) return;
    try {
      const token = await getAuthToken();
      if (!token) {
        showToast(CARD_UNSUPPORTED_MSG, 'exclamation-triangle');
        return;
      }
      const requestUrl = item.media_kind === 'video'
        ? standaloneVideoRequestUrl(item.output_key)
        : standaloneImageRequestUrl(item.output_key);
      const blob = await fetchCache.fetchBlob(requestUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (item.media_kind === 'video') {
        if (!item.source_image_output_key) {
          showToast(cardTranslation('card_gallery_video_needs_source', 'This video cannot be used from the gallery. Upload the file instead.'), 'exclamation-triangle');
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        assignSlotMedia(slot, {
          ext: 'webm',
          previewUrl: objectUrl,
          isVideoPreview: true,
          galleryOutputKey: String(item.output_key),
          galleryMediaKind: 'video',
        });
        hideCrop();
        return;
      }
      const mime = blob.type || 'image/webp';
      if (!isAllowedCardImageType(mime)) {
        showToast(CARD_UNSUPPORTED_MSG, 'exclamation-triangle');
        return;
      }
      const dataUrl = await blobToDataUrl(blob);
      galleryCropDraft = { slot, outputKey: String(item.output_key) };
      try {
        await beginStillCrop(slot, dataUrl);
      } catch (error) {
        galleryCropDraft = null;
        throw error;
      }
    } catch (error) {
      console.error('Card gallery pick failed:', error);
      showToast(CARD_UNSUPPORTED_MSG, 'exclamation-triangle');
    }
  };

  const openCardGallery = (slot) => {
    openCharacterCardGalleryPicker({
      selectedKey: cardGallerySelection[slot],
      onSelect: (item) => { void applyGalleryItem(slot, item); },
    });
  };

  sfwInput?.addEventListener('change', (event) => handleUploadChange('sfw', event));
  nsfwInput?.addEventListener('change', (event) => handleUploadChange('nsfw', event));
  document.getElementById('character-card-gallery-sfw')?.addEventListener('click', () => openCardGallery('sfw'));
  document.getElementById('character-card-gallery-nsfw')?.addEventListener('click', () => openCardGallery('nsfw'));

  applyBtn?.addEventListener('click', async () => {
    if (!cardCrop.active) return;
    try {
      const dataUrl = await cardCrop.exportDataUrl();
      const draft = galleryCropDraft?.slot === activeCardSlot ? galleryCropDraft : null;
      const crop = draft ? cardCrop.exportSourceRect() : null;
      if (!draft && getDataUrlDecodedByteLength(dataUrl) > MAX_CARD_STILL_BYTES) {
        showCardStillTooLarge();
        return;
      }
      assignSlotMedia(activeCardSlot, {
        stillDataUrl: dataUrl,
        videoDataUrl: null,
        ext: 'webp',
        previewUrl: dataUrl,
        isVideoPreview: false,
        galleryOutputKey: draft?.outputKey || null,
        galleryMediaKind: draft ? 'image' : null,
        crop,
      });
      hideCrop();
    } catch (error) {
      console.error('Card crop export failed:', error);
      showToast('Failed to apply cropped image.', 'exclamation-triangle');
    }
  });

  cancelBtn?.addEventListener('click', () => hideCrop());

  document.querySelectorAll('.preview-surface-clear').forEach((btn) => {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const slot = btn.dataset.slot === 'nsfw' ? 'nsfw' : 'sfw';
      clearCardSlot(slot);
    });
  });

  document.addEventListener('nsfwPreferenceSelected', syncNsfwCardSlotVisibility);
  syncNsfwCardSlotVisibility();
};

const bindForms = async () => {
  const step1Btn = document.getElementById('step1-submit');
  const step2Btn = document.getElementById('step2-submit');
  const step3Btn = document.getElementById('step3-submit');
  const step4Btn = document.getElementById('step4-submit');
  const step0Btn = document.getElementById('submit-ai-request');
  const skipBtn = document.getElementById('skip-ai-assistant');

  if (step1Btn) step1Btn.addEventListener('click', handleStep1);
  if (step2Btn) step2Btn.addEventListener('click', handleStep2);
  if (step3Btn) step3Btn.addEventListener('click', handleStep3);
  if (isSfwSite() && step4Btn && !document.getElementById('sfw-card-moderation-note')) {
    const note = document.createElement('p');
    note.id = 'sfw-card-moderation-note';
    note.className = 'media-crop-hint';
    note.textContent = 'Saving this card uses 1 credit for the SFW moderation check.';
    step4Btn.before(note);
  }
  if (step4Btn) step4Btn.addEventListener('click', handleStep4Submit);
  if (step0Btn) step0Btn.addEventListener('click', handleStep0);
  if (skipBtn) skipBtn.addEventListener('click', () => setActiveStep(1));
  bindPanelHelpTooltips();
  bindCharacterCardUpload();

  const generatePreviewBtn = document.getElementById('generate-preview-btn');
  const generateSlowPreviewBtn = document.getElementById('generate-preview-slow-btn');
  const closePreviewBtn = document.getElementById('close-preview-btn');
  const previewModal = document.getElementById('preview-modal');
  const closeModalBtn = document.getElementById('close-preview-modal');
  const visibilitySelect = document.getElementById('ai-character-visibility');
  const appearancePromptEl = document.getElementById('ai-character-prompt');
  const premiumAppearanceWarning = document.getElementById('premium-appearance-warning');

  const updatePremiumAppearanceWarning = () => {
    if (!premiumAppearanceWarning) return;
    const isPublic = (visibilitySelect?.value || 'public') === 'public';
    const matchedTag = isPublic
      ? findPremiumAppearanceTag(appearancePromptEl?.value || '')
      : null;
    if (!matchedTag) {
      premiumAppearanceWarning.hidden = true;
      premiumAppearanceWarning.textContent = '';
      return;
    }
    const t = getRecruitTranslations();
    const template = t.premium_appearance_warning
      || 'Your character will be visible only to yourself and paid users because of the tag {tag}';
    premiumAppearanceWarning.textContent = interpolate(template, { tag: matchedTag });
    premiumAppearanceWarning.hidden = false;
  };
  const getStoredQuotaSnapshot = () => getStoredQuota();
  const getRecruitTranslations = () => {
    const preferredLang = storage.get('displayLanguage');
    const runtimeLang = getCurrentLanguage?.();
    const globalLang = window.getCurrentLanguage?.();
    const candidates = [preferredLang, runtimeLang, globalLang, 'en'].filter(Boolean);
    for (const lang of candidates) {
      const section = window.translations?.[lang]?.['create-character'];
      if (section) return section;
    }
    return {};
  };

  const getCurrentFormState = () => {
    return {
      name: document.getElementById('ai-character-name')?.value?.trim() || '',
      gender: document.getElementById('ai-character-gender')?.value || '',
      visibility: document.getElementById('ai-character-visibility')?.value || 'public',
      bust: document.getElementById('ai-character-bust')?.value || '',
      appearance: document.getElementById('ai-character-prompt')?.value?.trim() || '',
      outfit: document.getElementById('ai-outfit-prompt')?.value?.trim() || '',
      appearanceNegative: document.getElementById('ai-appearance-negative')?.value?.trim() || '',
      outfitNegative: document.getElementById('ai-outfit-negative')?.value?.trim() || ''
    };
  };

  const formMatchesMode = (mode) => {
    const previous = formSnapshotByMode[mode];
    if (!previous) return false;
    return JSON.stringify(getCurrentFormState()) === JSON.stringify(previous);
  };

  const clearPreviewFormSnapshots = () => {
    formSnapshotByMode.fast = null;
    formSnapshotByMode.slow = null;
  };

  const syncSlowPreviewButtonLabel = () => {
    if (!generateSlowPreviewBtn) return;
    const isPrivate = (visibilitySelect?.value || 'public') === 'private';
    const t = getRecruitTranslations();
    const baseLabel = t.slow_preview || 'Slow preview';
    const bypassSuffix = isPrivate ? (t.slow_preview_bypass_suffix || ' (Bypass safety checks)') : '';
    generateSlowPreviewBtn.innerHTML = `<i class="fas fa-clock"></i> ${baseLabel}${bypassSuffix}`;
  };

  const shouldBlockFastPreview = () => {
    const loraId = document.getElementById('lora-id-input')?.value?.trim() || '';
    const appearance = document.getElementById('ai-character-prompt')?.value?.trim() || '';
    const outfit = document.getElementById('ai-outfit-prompt')?.value?.trim() || '';
    return Boolean(loraId) || hasFastPreviewBlockingTags(appearance, outfit);
  };

  const getFastPreviewBlockReason = () => {
    const loraId = document.getElementById('lora-id-input')?.value?.trim() || '';
    if (loraId) return 'lora';

    const appearance = document.getElementById('ai-character-prompt')?.value?.trim() || '';
    const outfit = document.getElementById('ai-outfit-prompt')?.value?.trim() || '';
    if (hasFastPreviewBlockingTags(appearance, outfit)) return 'prohibited';

    return null;
  };

  const getProhibitedTagsMessage = () => {
    const appearance = document.getElementById('ai-character-prompt')?.value?.trim() || '';
    const outfit = document.getElementById('ai-outfit-prompt')?.value?.trim() || '';
    const tags = getFastPreviewBlockingTags(appearance, outfit);
    const t = getRecruitTranslations();
    const template = t.fast_preview_disabled_prohibited_tags
      || 'Fast preview is unavailable when you use "{tags}". Use slow preview instead.';
    return interpolate(template, { tags: tags.join(', ') });
  };

  const getFastPreviewDisabledMessage = () => {
    const t = getRecruitTranslations();
    const reason = getFastPreviewBlockReason();
    if (reason === 'lora') {
      return t.lora_note || "Please note: LoRA doesn't work with fast preview.";
    }
    if (reason === 'prohibited') {
      return getProhibitedTagsMessage();
    }
    return t.fast_preview_quota_exhausted || 'No fast previews remaining.';
  };

  const showFastPreviewDisabledFeedback = () => {
    showToast(getFastPreviewDisabledMessage(), 'exclamation-triangle');
  };

  const setFastPreviewBtnDisabled = (fastBtn, isDisabled) => {
    fastBtn.classList.toggle('disabled', isDisabled);
    fastBtn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    fastBtn.disabled = false;

    if (isDisabled) {
      fastBtn.title = getFastPreviewDisabledMessage();
    } else {
      fastBtn.removeAttribute('title');
    }
  };

  const handlePreviewGeneration = async ({ forceSlowPreview = false } = {}) => {
    const oauthManager = initOAuthManager();
    if (!(await oauthManager.isAuthenticated())) {
      try {
        const controller = window.loginModalController || (await import('../components/LoginModalController.js')).default;
        controller?.show?.();
      } catch (e) {
        console.error('Login modal failed:', e);
      }
      return;
    }

    if (!forceSlowPreview && shouldBlockFastPreview()) {
      showFastPreviewDisabledFeedback();
      return;
    }

    const appearance = document.getElementById('ai-character-prompt')?.value?.trim() || '';
    const outfitPromptEl = document.getElementById('ai-outfit-prompt');
    const aiReq = document.getElementById('ai-request');
    const name = document.getElementById('ai-character-name')?.value?.trim() || '';
    const gender = document.getElementById('ai-character-gender')?.value || 'female';

    if (!appearance) {
      showToast('Please provide an appearance prompt', 'exclamation-triangle');
      return;
    }
    const appearanceNegative = document.getElementById('ai-appearance-negative')?.value?.trim() || '';
    const outfitNegative = document.getElementById('ai-outfit-negative')?.value?.trim() || '';
    const nsfwBlock = getBlockedNsfwPromptMessage([appearance, outfitPromptEl?.value || '', appearanceNegative, outfitNegative].filter(Boolean).join(', '));
    if (nsfwBlock) {
      showToast(nsfwBlock, 'exclamation-triangle', { duration: 0, closable: true });
      return;
    }
    const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage([appearance, outfitPromptEl?.value || '', appearanceNegative, outfitNegative].filter(Boolean).join(', '));
    if (unrecognizedBlock) {
      showToast(unrecognizedBlock, 'exclamation-triangle', { duration: 0, closable: true });
      return;
    }

    // Update modal title via gender-aware translation
    if (window.applyGenderTranslations) {
      window.applyGenderTranslations('create-character');
    } else {
      const modalTitle = document.getElementById('preview-modal-title');
      if (modalTitle) modalTitle.textContent = gender === 'male' ? 'Is that him?' : 'Is that her?';
    }

    const isRealism = getCharacterFormStyle() === 'realism';
    const qualityWarning = document.getElementById('preview-quality-warning');
    const previewModalTitle = document.getElementById('preview-modal-title');
    qualityWarning?.classList.toggle('is-hidden', !isRealism);
    if (previewModalTitle) previewModalTitle.style.margin = isRealism ? '0' : '';

    const previewMode = forceSlowPreview ? 'slow' : 'fast';
    const requestSerial = beginCharacterPreviewRequest(previewMode);
    if (formMatchesMode(previewMode)) {
      const revealed = await revealCharacterPreview(previewMode);
      if (revealed) {
        setPreviewYesWaiting(!previewHasImage());
        if (previewModal) previewModal.classList.add('is-active');
        refreshStep1CreditInfo().catch(() => {});
        return;
      }
    }
    if (!isCurrentCharacterPreviewRequest(requestSerial)) return;

    if (aiReq && !aiReq.value.trim()) {
      aiReq.value = appearance || name || '';
    }
    if (outfitPromptEl && !outfitPromptEl.value.trim()) {
      outfitPromptEl.value = appearance || 'default outfit';
    }

    if (!isCurrentCharacterPreviewRequest(requestSerial)) return;
    formSnapshotByMode[previewMode] = getCurrentFormState();
    state.hasPreview = false;
    setPreviewYesWaiting(true);
    setPreviewLoadingState('ai-character-prompt-preview', true);
    if (previewModal) previewModal.classList.add('is-active');
    refreshStep1CreditInfo().catch(() => {});
    await startCharacterPreviewGeneration({
      previewMode,
      characterVisibility: visibilitySelect?.value || 'public',
      requestSerial,
    });
  };

  if (generatePreviewBtn) {
    generatePreviewBtn.addEventListener('click', () => {
      if (generatePreviewBtn.classList.contains('disabled')) {
        showFastPreviewDisabledFeedback();
        return;
      }
      handlePreviewGeneration();
    });
  }

  if (generateSlowPreviewBtn) {
    generateSlowPreviewBtn.addEventListener('click', () => handlePreviewGeneration({ forceSlowPreview: true }));
  }

  document.getElementById('skip-look-step')?.addEventListener('click', () => {
    if (!state.isUpdate) return;
    restoreSavedLook();
    clearPreviewFormSnapshots();
    clearCharacterLookPreview();
    document.getElementById('ai-character-prompt')?.dispatchEvent(new Event('input', { bubbles: true }));
    setActiveStep(2);
  });

  if (visibilitySelect) {
    visibilitySelect.addEventListener('change', () => {
      clearPreviewFormSnapshots();
      updatePreviewBtnsFromQuota(getStoredQuotaSnapshot());
      updatePremiumAppearanceWarning();
    });
  }
  if (appearancePromptEl) {
    appearancePromptEl.addEventListener('input', () => {
      clearPreviewFormSnapshots();
      updatePremiumAppearanceWarning();
    });
  }
  document.getElementById('ai-outfit-prompt')?.addEventListener('input', clearPreviewFormSnapshots);
  document.getElementById('ai-character-gender')?.addEventListener('change', clearPreviewFormSnapshots);
  document.getElementById('ai-character-style')?.addEventListener('change', clearPreviewFormSnapshots);
  updatePremiumAppearanceWarning();
  syncSlowPreviewButtonLabel();

  await loadDanbooruTags();

  const refreshPreviewBtnState = () => {
    updatePreviewBtnsFromQuota(getStoredQuotaSnapshot());
    void refreshLookCreditInfo();
  };
  ['lora-id-input', 'lora-strength-input', 'ai-character-prompt', 'ai-outfit-prompt', 'ai-appearance-negative', 'ai-outfit-negative'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener('input', refreshPreviewBtnState);
  });
  document.getElementById('ai-character-style')?.addEventListener('change', refreshPreviewBtnState);

  // Update fast and slow preview button labels independently from quota state.
  const setPreviewBtnDisabled = (btn, isDisabled) => {
    btn.disabled = isDisabled;
    btn.classList.toggle('disabled', isDisabled);
    if (isDisabled) btn.setAttribute('aria-disabled', 'true');
    else btn.removeAttribute('aria-disabled');
  };

  const updatePreviewBtnsFromQuota = (quota) => {
    const fastBtn = document.getElementById('generate-preview-btn');
    const slowBtn = document.getElementById('generate-preview-slow-btn');
    if (!fastBtn || !slowBtn) return;

    const t = getRecruitTranslations();
    const isFastBlocked = shouldBlockFastPreview();

    if (!quota) {
      fastBtn.innerHTML = `<i class="fas fa-magic"></i> ${t.show_me || 'Show Me'}`;
      setFastPreviewBtnDisabled(fastBtn, isFastBlocked);
      syncSlowPreviewButtonLabel();
      setPreviewBtnDisabled(slowBtn, false);
      if (isCharacterSlowPreviewQueued()) {
        slowBtn.innerHTML = '<i class="fas fa-clock"></i> <span data-translate="generate_queued">In queue…</span>';
      }
      lockPreviewButtonsWhenLookUnchanged(fastBtn);
      return;
    }

    const fastTpl = t.generate_fast_remaining || '{{count}}/{{total}} free fast left';
    const fastRemaining = Math.max(0, Number(quota.fast_remaining ?? 0));
    const fastTotal = Math.max(0, Number(quota.fast_total ?? 0));
    const isFastInitial = fastTotal > 0 && fastRemaining === fastTotal;
    fastBtn.innerHTML = isFastInitial
      ? `<i class="fas fa-magic"></i> ${t.show_me || 'Show Me'}`
      : `<i class="fas fa-magic"></i> ${fastTpl.replace('{{count}}', fastRemaining).replace('{{total}}', fastTotal)}`;
    setFastPreviewBtnDisabled(fastBtn, isFastBlocked || fastRemaining <= 0);

    const slowRemaining = Math.max(0, Number(quota.slow_remaining ?? 0));
    const slowTotal = Math.max(0, Number(quota.slow_total ?? 0));
    const isPrivate = (visibilitySelect?.value || 'public') === 'private';
    const bypassSuffix = isPrivate ? (t.slow_preview_bypass_suffix || ' (Bypass safety checks)') : '';
    const baseSlowLabel = `${t.slow_preview || 'Slow preview'}${bypassSuffix}`;
    const isSlowInitial = slowTotal > 0 && slowRemaining === slowTotal;
    if (slowRemaining <= 0) {
      const paidTpl = t.slow_preview_credits || 'Slow preview · {{cost}} credits';
      const cost = computeStandaloneImageCost({
        style: getCharacterFormStyle(),
        loraCount: 0,
        width: 896,
        height: 1152,
      });
      slowBtn.innerHTML = `<i class="fas fa-clock"></i> ${paidTpl.replace('{{cost}}', cost)}${bypassSuffix}`;
      setPreviewBtnDisabled(slowBtn, false);
    } else {
      const slowTpl = t.generate_slow_remaining_count || '{{count}}/{{total}} free slow left';
      slowBtn.innerHTML = isSlowInitial
        ? `<i class="fas fa-clock"></i> ${baseSlowLabel}`
        : `<i class="fas fa-clock"></i> ${slowTpl.replace('{{count}}', slowRemaining).replace('{{total}}', slowTotal)}${bypassSuffix}`;
      setPreviewBtnDisabled(slowBtn, false);
    }

    if (isCharacterSlowPreviewQueued()) {
      slowBtn.innerHTML = '<i class="fas fa-clock"></i> <span data-translate="generate_queued">In queue…</span>';
      setPreviewBtnDisabled(slowBtn, false);
    }
    lockPreviewButtonsWhenLookUnchanged(fastBtn);
  };

  document.addEventListener('genQuotaUpdated', (e) => updatePreviewBtnsFromQuota(e.detail));
  document.addEventListener('languageSwitched', () => {
    updatePreviewBtnsFromQuota(getStoredQuotaSnapshot());
    updatePremiumAppearanceWarning();
    void refreshLookCreditInfo();
  });

  // Restore from user_gen_quota on page refresh.
  updatePreviewBtnsFromQuota(getStoredQuotaSnapshot());
  loadCreditPricing()
    .then(() => updatePreviewBtnsFromQuota(getStoredQuotaSnapshot()))
    .catch(() => {});
  // Initial page load can run before translation bundles finish.
  // Retry briefly so non-English default languages update correctly.
  let previewLabelHydrationAttempts = 0;
  const hydratePreviewLabels = () => {
    previewLabelHydrationAttempts += 1;
    updatePreviewBtnsFromQuota(getStoredQuotaSnapshot());
    updatePremiumAppearanceWarning();
    const targetLang = storage.get('displayLanguage');
    const hasTargetBundle = targetLang ? !!window.translations?.[targetLang]?.['create-character'] : true;
    if (hasTargetBundle || previewLabelHydrationAttempts >= 12) return;
    setTimeout(hydratePreviewLabels, 250);
  };
  hydratePreviewLabels();

  if (closePreviewBtn) {
    closePreviewBtn.addEventListener('click', () => {
      previewModal.classList.remove('is-active');
    });
  }

  if (closeModalBtn && previewModal) {
    closeModalBtn.addEventListener('click', () => {
      previewModal.classList.remove('is-active');
    });
  }

  // Close modal when clicking outside content
  if (previewModal) {
    previewModal.addEventListener('click', (e) => {
      if (e.target === previewModal) {
        previewModal.classList.remove('is-active');
      }
    });
  }
};

const resetRecruit = async () => {
  state.characterId = null;
  state.completedSteps.clear();
  state.hasPreview = false;
  state.currentStep = 0;
  state.isCharacterPrivate = false;
  state.isUpdate = false;
  state.savedLook = null;
  state.savedStep2 = null;
  setLookFieldsLocked(false);
  setUpdateLookChrome(false);
  formSnapshotByMode.fast = null;
  formSnapshotByMode.slow = null;
  resetCharacterPreviewSessions();
  parkCharacterIdentityRow('character-identity-slot-step1');
  clearExistingCharacterOutfits();

  setStoredStep(0);

  const form = document.getElementById('character-form');
  if (form) {
    const elements = form.querySelectorAll('input, textarea, select, button');
    elements.forEach((el) => {
      if (el instanceof HTMLInputElement) {
        el.disabled = false;
        if (el.type === 'checkbox') el.checked = false;
        else el.value = '';
      } else if (el instanceof HTMLTextAreaElement) {
        el.disabled = false;
        el.value = '';
      } else if (el instanceof HTMLSelectElement) {
        el.disabled = false;
        el.selectedIndex = 0;
      } else if (el instanceof HTMLButtonElement) {
        el.disabled = false;
      }
    });
  }

  // Ensure prompt is cleared explicitly
  const prompt = document.getElementById('ai-character-prompt');
  if (prompt) prompt.value = '';

  const queuedPanel = document.getElementById('character-queued-panel');
  if (queuedPanel) queuedPanel.classList.add('is-hidden');
  document.getElementById('character-queued-inline-card')?.classList.add('is-hidden');
  document.getElementById('character-updated-card')?.classList.add('is-hidden');
  resetPendingCards();
  const aiReq = document.getElementById('ai-request');
  if (aiReq) aiReq.value = '';
  const aiLoading = document.getElementById('ai-loading');
  if (aiLoading) aiLoading.classList.add('is-hidden');

  setActiveStep(0);

  storage.remove('character_draft_v1');
  clearQueuedCardTimers();
  clearQueuedCharacterState();

  // Reset initialization flags to allow re-initialization
  delete window.__characterFormInitDone__;

  // re-init form data and assistant (force re-initialization on reset)
  initializeCharacterForm().catch((e) => console.warn('init form failed', e));
  initializeCharacterAIAssistant(true).catch((e) => console.warn('init ai assistant failed', e));
};

const runInit = async () => {
  if (isRecruitQueueDebug()) {
    try {
    } catch { }
  }

  let initialStep = 0;
  const stored = getStoredStep();
  if (stored !== null) {
    const num = parseInt(stored, 10);
    if (!Number.isNaN(num) && num >= 0 && num <= 3) initialStep = num;
  }

  const forceQueuedCompletionView = async () => {
    try {
      const queued = getQueuedCharacterState();
      if (!queued || !queued.characterId || !queued.startedAt) return false;


      // Force wizard step + queued panel
      setActiveStep(5);
      const queuedPanel = document.getElementById('character-queued-panel');
      if (queuedPanel) queuedPanel.classList.remove('is-hidden');

      // Render inline queued card (idempotent)
      await renderQueuedCharacterCardInline();
      return true;
    } catch (e) {
      console.warn('[Recruit] Failed to force queued completion view', e);
      return false;
    }
  };

  // If there's an active queued character, prefer the completion view immediately
  try {
    const queued = getQueuedCharacterState();
    if (queued && queued.characterId && queued.startedAt) {
      initialStep = 5;
      const queuedPanel = document.getElementById('character-queued-panel');
      if (queuedPanel) queuedPanel.classList.remove('is-hidden');
    }
  } catch { }

  setActiveStep(initialStep);
  initSfwLoraLock();
  await bindForms();

  // Resume any pending slow-path image generation (page refresh recovery)
  import('./imageGeneration.js').then(({ resumePendingGeneration }) => {
    resumePendingGeneration('ai-character-prompt-preview').catch(() => {});
  }).catch(() => {});

  // Ensure the "Searching…" indicator never shows unless an active request is running.
  const step0Loading = document.getElementById('ai-loading');
  if (step0Loading) {
    step0Loading.classList.add('is-hidden');
  }

  const resetBtn = document.getElementById('reset-draft-btn');
  if (resetBtn && !resetBtn.dataset.bound) {
    resetBtn.dataset.bound = 'true';
    resetBtn.addEventListener('click', resetRecruit);
  }

  // Initialize step2 KV table with default rows
  initStep2KvTable();
  const addRowBtn = document.getElementById('step2-add-row');
  if (addRowBtn) {
    addRowBtn.addEventListener('click', () => {
      const table = document.getElementById('step2-kv-table');
      if (table) table.appendChild(createKvRow());
      refreshKvCounter();
    });
  }

  try {
    await initializeCharacterForm();
  } catch (e) {
    console.warn('init form failed', e);
  }
  await forceQueuedCompletionView().catch(() => { });
  initializeCharacterAIAssistant().catch((e) => console.warn('init ai assistant failed', e));

  // Booru-style autocomplete
  initializeNegativePromptAutocomplete('ai-appearance-negative')
    .catch((e) => console.warn('init appearance negative autocomplete failed', e));
  initializeNegativePromptAutocomplete('ai-outfit-negative')
    .catch((e) => console.warn('init outfit negative autocomplete failed', e));
  initializePromptAutocomplete('ai-character-prompt', 'characterPrompt', null, {
    forbiddenCategories: [6, 7],
    forbidAppearanceCountTags: true,
  }).catch((e) => console.warn('init prompt autocomplete failed', e));
  initializeOutfitPromptAutocomplete('ai-outfit-prompt')
    .catch((e) => console.warn('init outfit autocomplete failed', e));

  // Voice dropdown + voice preview (gender-aware; shows Play Sample when available)
  initializeVoicePreview('ai-character-voice', 'voice-sample-container');

  const genderSelect = document.getElementById('ai-character-gender');

  if (genderSelect) {
    genderSelect.addEventListener('change', () => {
      if (window.applyGenderTranslations) {
        window.applyGenderTranslations('create-character');
      }
    });
  }

  // Sync the art-style dropdown with the user's stored preference and persist changes.
  const styleSelect = document.getElementById('ai-character-style');
  if (styleSelect) {
    styleSelect.value = getStylePreference();
    styleSelect.addEventListener('change', () => setStylePreference(getCharacterFormStyle()));
  }

  // track preview creation via mutation observer
  const previewContainer = document.getElementById('ai-character-prompt-preview');
  const markPreview = () => {
    if (!previewHasImage()) return;
    state.hasPreview = true;
    setPreviewYesWaiting(false);
  };
  markPreview();
  if (previewContainer) {
    const observer = new MutationObserver(() => markPreview());
    observer.observe(previewContainer, { attributes: true, childList: true, subtree: true });
  }

  // Restore queued character card if the user navigates away and comes back
  renderQueuedCharacterCardInline().catch(() => { });

  // Some init paths (draft restoration in characterForm.js) can flip the visible step back to 0.
  // Re-assert queued completion view after the current tick so we "win last" if queue exists.
  setTimeout(() => {
    forceQueuedCompletionView().catch(() => { });
  }, 0);

  mountOwnedItemEditPicker({
    root: document.getElementById('owned-character-edit-panel'),
    kind: 'character',
    titleFallback: 'Or edit a character you previously created',
    validateFallback: 'Edit this character',
    fetchList: fetchOwnedCharactersForEdit,
    fetchItem: fetchOwnedCharacterForEdit,
    onValidate: async (item) => {
      await applyOwnedCharacterToForm(item);
      setActiveStep(1);
    },
  });

  document.addEventListener('languageSwitched', () => {
    if (!document.getElementById('character-updated-card')?.classList.contains('is-hidden')) {
      fillCharacterUpdatedSub();
    }
  });
};

// Debug keyboard navigation - allow left/right arrows to navigate between steps
document.addEventListener('keydown', (event) => {
  // Only work when no input/textarea is focused
  const activeElement = document.activeElement;
  const isInputFocused = !!(activeElement && (
    activeElement.tagName === 'INPUT' ||
    activeElement.tagName === 'TEXTAREA' ||
    (activeElement instanceof HTMLElement && activeElement.isContentEditable)
  ));

  if (isInputFocused) return;

  const currentStep = state.currentStep;
  let newStep = currentStep;

  if (event.key === 'ArrowLeft' && currentStep > 0) {
    newStep = currentStep - 1;
    event.preventDefault();
  } else if (event.key === 'ArrowRight' && currentStep < 5) {
    newStep = currentStep + 1;
    event.preventDefault();
  }

  if (newStep !== currentStep) {
    setActiveStep(newStep);
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInit);
} else {
  runInit();
}

